import { SearchIcon } from '@heroicons/react/solid'
import Fuse from 'fuse.js'
import { uniq } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { FixedSizeList } from 'react-window'
import BaseInput from 'shared/components/base/form/base-input'
import BaseSelect from 'shared/components/base/form/base-select'
import dictionary from 'shared/constants/dictionary'
import {
  useEmit,
  useSocketInstance,
} from 'shared/contexts/socket/socket-context'
import OperatorExtension from 'store/slice/call-detail/types'
import { RootState } from 'store/store'

import OperatorCard from './OperatorCard'

type Props = {
  selectedOperator: Partial<OperatorExtension> | null
  setSelectedOperator: (payload?: any) => any
  defaultSearch?: string
  title?: string | null
}

const OperatorsList = (props: Props) => {
  // props
  const { defaultSearch, selectedOperator, setSelectedOperator, title } = props

  // states
  const [allOperators, setAllOperators] = useState<
    Partial<OperatorExtension>[]
  >([])
  const [search, setSearch] = useState<string>(defaultSearch || '')
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all')

  // hooks
  const emit = useEmit()
  const socket = useSocketInstance()
  const isHangup = useSelector(
    (state: RootState) => state.callDetail.callData.isHangup
  )

  // refs
  const operatorsListRef = useRef<any>()

  // memo
  const orderedOperators = useMemo(() => {
    let result = allOperators

    if (search) result = filterOperatorsBasedOnSearch(allOperators, search)
    if (selectedRoleFilter)
      result = filterOperatorsBasedOnRole(result, selectedRoleFilter)

    return result
  }, [allOperators, search, selectedRoleFilter])

  const roleFilterOptions = useMemo(() => {
    const allTeamsNames = allOperators.map((item) => item.teamName)
    return [
      { label: 'همه', key: 'all', value: 'all' },
      ...uniq(allTeamsNames).map((teamName) => ({
        label: teamName || '',
        key: teamName || '',
        value: teamName || '',
      })),
    ]
  }, [allOperators])

  // functions
  function filterOperatorsBasedOnSearch(
    _operators: Partial<OperatorExtension>[],
    _search: string
  ) {
    if (!isNaN(Number(_search))) {
      return _operators.filter((_operator) =>
        _operator?.userExtension?.includes(_search)
      )
    }
    const fuse = new Fuse(_operators, {
      threshold: 0.4,
      ignoreLocation: true,
      keys: [
        'userPosition',
        'fullName',
        'product_role',
        'searchName',
        'userPositionLevel',
      ],
    })
    return fuse.search(_search).map((fuseResult) => fuseResult.item)
  }

  function filterOperatorsBasedOnRole(
    _operators: Partial<OperatorExtension>[],
    role?: string
  ) {
    if (role !== 'all') {
      return _operators.filter((operator) => operator?.teamName === role)
    }
    return _operators
  }

  // effects
  useEffect(() => {
    emit('get-operators')
      .then((extensions: OperatorExtension[]) => {
        if (Array.isArray(extensions) && extensions?.length) {
          setAllOperators(extensions)
        }
      })
      .catch(() => {
        toast.error(dictionary.socket_timeout)
      })
    socket?.on('extensionStatus', (extensions: OperatorExtension[]) => {
      if (Array.isArray(extensions) && extensions?.length) {
        setAllOperators(extensions)
      }
    })

    return () => {
      socket?.off('extensionStatus')
    }
  }, [])

  useEffect(() => {
    if (defaultSearch && defaultSearch !== search) {
      setSelectedRoleFilter('all')
      setSearch(defaultSearch)
    }
  }, [defaultSearch])

  const OperatorRow = ({ index, style }: any) => (
    <div style={{ ...style, right: '0' }}>
      <OperatorCard
        onSelect={(op) => setSelectedOperator(op)}
        isSelected={selectedOperator?.id === orderedOperators[index]?.id}
        operator={orderedOperators[index]}
        index={index + 1}
        key={index}
      />
    </div>
  )

  return (
    <>
      {isHangup && (
        <div className="absolute inset-0 max-h-[40rem] bg-color-overlay bg-opacity-[48%] flex justify-center items-center rounded-lg hover:cursor-not-allowed z-[100]">
          <p className="font-semibold text-sm leading-8 text-white ">
            به دلیل قطع تماس، امکان اتصال به شخص دیکر وجود ندارد
          </p>
        </div>
      )}
      <div className="text-[16px] text-color-title text-right font-semibold mb-4">
        {title || 'لیست کارشناسان بازرسی'}
      </div>

      <div className="flex mb-[32px]">
        <BaseInput
          trailingIcon={SearchIcon}
          className="w-[355px]"
          onChange={(event) => setSearch(event.target.value)}
          value={search}
        />
        <BaseSelect<string>
          value={selectedRoleFilter}
          options={roleFilterOptions}
          onSelect={setSelectedRoleFilter}
          className="mr-2 w-[200px]"
        />
      </div>

      {orderedOperators.length < 1 && allOperators.length > 0 ? (
        <p className="text-center">موردی یافت نشد</p>
      ) : (
        <FixedSizeList
          ref={operatorsListRef}
          direction="rtl"
          height={464}
          itemSize={100}
          itemCount={orderedOperators?.length}
          width="100%"
          className="overflow-y-visible overflow-x-hidden space-y-3 soft-scrollbar -ml-4 pl-4"
          innerElementType={(props) => (
            <div {...props} className={`relative ${props.className}`}>
              {props.children}
            </div>
          )}
        >
          {OperatorRow}
        </FixedSizeList>
      )}
    </>
  )
}

export default OperatorsList
